/*
 * ITERCATUS Reporter — Sync engine (Phase 9.2)
 *
 * Flushes the IndexedDB offline queue to the server with:
 *  - Idempotency: each item's stable client_id is sent so the backend
 *    deduplicates (no duplicate Cloudinary assets / records / notes).
 *  - Safe exponential backoff per item (never aggressive infinite retry).
 *  - Connectivity-aware: auto-retry on "online" events + manual "retry now".
 *  - After confirmed upload + server metadata persisted, the heavy local
 *    binary is dropped (markDone).
 */
import * as Q from "./reporterQueue";
import {
  uploadCapture,
  createReporterNote,
  uploadVoiceNote,
  processVoiceNote,
  createLocationEvent,
} from "./reporterApi";

let _running = false;

async function _send(item, sessionId) {
  if (item.kind === "photo") {
    const fd = new FormData();
    fd.append("session_id", sessionId);
    fd.append("client_id", item.client_id);
    if (item.blob) fd.append("file", item.blob, `${item.client_id}.jpg`);
    if (item.payload.exif_datetime) fd.append("exif_datetime", item.payload.exif_datetime);
    if (item.payload.capture_timestamp) fd.append("capture_timestamp", item.payload.capture_timestamp);
    if (item.payload.note) fd.append("note", item.payload.note);
    if (item.payload.manual_day_id) fd.append("manual_day_id", item.payload.manual_day_id);
    if (item.payload.orientation != null) fd.append("orientation", String(item.payload.orientation));
    const res = await uploadCapture(fd);
    return res.data;
  }
  if (item.kind === "note" || item.kind === "impression") {
    const res = await createReporterNote({
      client_id: item.client_id,
      type: item.kind === "impression" ? "impression" : "note",
      text: item.payload.text,
      day_id: item.payload.day_id,
    });
    return res.data;
  }
  if (item.kind === "voice") {
    const fd = new FormData();
    fd.append("session_id", sessionId);
    fd.append("client_id", item.client_id);
    if (item.blob) fd.append("file", item.blob, `${item.client_id}.webm`);
    if (item.payload.day_id) fd.append("day_id", item.payload.day_id);
    const res = await uploadVoiceNote(fd);
    // Kick off transcription -> analysis pipeline (server-side, batch order).
    const vid = res.data?.voice_note?.id;
    if (vid && !res.data?.idempotent_replay) {
      try {
        await processVoiceNote(vid);
      } catch (_e) {
        /* transcription can be retried later; audio is safely stored */
      }
    }
    return res.data;
  }
  if (item.kind === "location") {
    const res = await createLocationEvent(sessionId, item.payload);
    return res.data;
  }
  throw new Error("unknown_kind");
}

export async function flushQueue(sessionId) {
  if (_running || !sessionId) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  _running = true;
  try {
    const items = await Q.pending();
    const now = Date.now();
    for (const item of items) {
      if (!Q.isReady(item, now)) continue;
      try {
        const data = await _send(item, sessionId);
        await Q.markDone(item.client_id, data);
      } catch (e) {
        const attempts = (item.attempts || 0) + 1;
        await Q.update(item.client_id, {
          attempts,
          next_attempt_at: Date.now() + Q.backoffDelay(attempts),
          error: (e && e.message) || "sync_error",
        });
      }
    }
  } finally {
    _running = false;
  }
}

export function installAutoSync(getSessionId) {
  const handler = () => flushQueue(getSessionId());
  window.addEventListener("online", handler);
  const interval = setInterval(handler, 15000);
  return () => {
    window.removeEventListener("online", handler);
    clearInterval(interval);
  };
}
